import { useEffect, useRef } from 'react';
const seenScopes = new Set();

// Half the prompt must stay visible for 600 ms. An iframe hidden by its parent
// or a background tab is not an impression, and reopening does not count twice.
export default function useVisibleImpression(onView, scope, enabled = true) {
  const element = useRef(null), callback = useRef(onView);
  callback.current = onView;
  useEffect(() => {
    const node = element.current;
    if (!enabled || !node || seenScopes.has(scope)) return;
    let timer, intersecting = false;
    const visible = () => {
      if (document.visibilityState === 'hidden' || !node.getClientRects().length) return false;
      try { if (window.frameElement && !window.frameElement.getClientRects().length) return false; } catch {}
      return true;
    };
    const cancel = () => { clearTimeout(timer); timer = null; };
    const inspect = () => {
      if (!intersecting || !visible()) { cancel(); return; }
      if (timer || seenScopes.has(scope)) return;
      timer = setTimeout(() => {
        timer = null;
        if (intersecting && visible() && !seenScopes.has(scope) && callback.current() !== false) {
          if (seenScopes.size >= 500) seenScopes.delete(seenScopes.values().next().value);
          seenScopes.add(scope);
        }
      }, 600);
    };
    const bounds = () => {
      const r = node.getBoundingClientRect();
      const width = Math.max(0, Math.min(r.right, window.innerWidth) - Math.max(0, r.left));
      const height = Math.max(0, Math.min(r.bottom, window.innerHeight) - Math.max(0, r.top));
      intersecting = r.width > 0 && r.height > 0 && width * height / (r.width * r.height) >= 0.5;
      inspect();
    };
    const observer = window.IntersectionObserver ? new IntersectionObserver(entries => {
      intersecting = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5);
      inspect();
    }, { threshold: [0, 0.5] }) : null;
    observer?.observe(node);
    const visibility = () => { if (observer) inspect(); else bounds(); };
    document.addEventListener('visibilitychange', visibility);
    if (!observer) { bounds(); window.addEventListener('scroll', bounds, true); window.addEventListener('resize', bounds); }
    return () => {
      cancel(); observer?.disconnect(); document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('scroll', bounds, true); window.removeEventListener('resize', bounds);
    };
  }, [scope, enabled]);
  return element;
}
